import type { TagColorId } from '../design/tagPalette';

/**
 * Every colour id the old palette stored, to the colour it reads as now, or
 * null for none (a grey, or nothing worth keeping: the folder then wears its
 * nearest coloured ancestor's).
 *
 * The default table of the palette migration (tag palette migration,
 * sections 1 and 2): the nearest new colour in OKLCH hue, a grey to none. The
 * app rewrites stored ids once, with per-folder overrides on top; until a
 * vault has been rewritten its shards still carry these, and a reader maps
 * them here so the web draws the new palette either way.
 *
 * `olive`, `sky` and `magenta` are old ids the new palette kept for the same
 * family, so they map onto themselves. The old `aqua` was another colour and
 * reads as Lagoon; the new Aqua is stored as `aquamarine`.
 */
export const LEGACY_TAG_COLORS: Readonly<Record<string, TagColorId | null>> = {
  purple: 'indigo',
  blue: 'cobalt',
  cyan: 'sky',
  green: 'aquamarine',
  yellow: 'citron',
  orange: 'brick',
  red: 'brick',
  pink: 'magenta',
  amethyst: 'orchid',
  'royal-blue': 'cobalt',
  aqua: 'lagoon',
  emerald: 'pine',
  topaz: 'tangerine',
  ruby: 'brick',
  garnet: 'brick',
  coral: 'tangerine',
  lilac: 'orchid',
  periwinkle: 'cobalt',
  sky: 'sky',
  mint: 'pine',
  lemon: 'citron',
  peach: 'tangerine',
  'candy-pink': 'magenta',
  turquoise: 'aquamarine',
  'purple-2': 'orchid',
  'blue-2': 'cobalt',
  'cyan-2': 'sky',
  'green-2': 'fern',
  'yellow-2': 'olive',
  lime: 'fern',
  'red-2': 'brick',
  magenta: 'magenta',
  'green-dark': 'pine',
  'green-3': 'fern',
  olive: 'olive',
  jade: 'pine',
  'moss-green': 'olive',
  sage: 'olive',
  forest: 'olive',
  'neon-purple': 'indigo',
  'neon-blue': 'cobalt',
  'neon-cyan': 'aquamarine',
  'neon-green': 'pine',
  'neon-yellow': 'olive',
  'neon-orange': 'tangerine',
  'neon-pink': 'brick',
  'neon-red': 'brick',
  'mono-white': null,
  'mono-light': null,
  'mono-silver': null,
  'mono-gray': null,
  slate: 'indigo',
  'mono-charcoal': null,
  'mono-dark': null,
  'mono-black': null,
  'dynamic-1': 'aquamarine',
  'dynamic-2': null,
  'dynamic-3': 'indigo',
  primary: 'aquamarine',
  secondary: null,
  tertiary: 'indigo',
  'paper-research': 'olive',
  'paper-deadlock': 'tangerine',
  'paper-bodywork': 'tangerine',
  'paper-compassion': 'cobalt',
  'paper-structure': 'cobalt',
  'paper-lavender': 'orchid',
  'terra-cotta': 'brick',
  ochre: 'tangerine',
  umber: 'tangerine',
  moss: 'pine',
  plum: 'orchid',
  sand: 'tangerine',
  sapphire: 'cobalt',
  lavender: null,
  'orange-2': 'brick',
  'aqua-dark': 'aquamarine',
  'cyber-acid': 'fern',
  'cyber-matrix': 'fern',
  'cyber-toxic': 'pine',
  'cyber-ice': 'sky',
  'cyber-volt': 'cobalt',
  'cyber-deep': 'cobalt',
  'cyber-violet': 'indigo',
  'cyber-ghost': 'pine',
  'pastel-mint': null,
};
